export type LabelAnchor =
  | 'center'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

export interface LabelPosition {
  anchor: LabelAnchor;
  offset: [number, number];
}

const DEFAULT_POSITION: LabelPosition = {
  anchor: 'bottom',
  offset: [0, -4],
};

// 8-octant lookup. Index = round(opposite / 45) mod 8, where `opposite` is the
// direction the label should sit (heading + 180°). Anchor names the LABEL edge
// touching the point, so `bottom` puts the label above. Offset pushes the
// label that many px further from the point in the opposite direction so the
// pill clears the 7px-radius dot + 2px stroke. Cardinals get a single-axis
// push of 5px; diagonals split it as 3/3 so the corner stays ~5px from the
// point.
const PUSH = 5;
const DIAG = 3;
const OCTANTS: LabelPosition[] = [
  { anchor: 'bottom', offset: [0, -PUSH] }, // 0°  N: heading=S, label above
  { anchor: 'bottom-left', offset: [DIAG, -DIAG] }, // 45° NE
  { anchor: 'left', offset: [PUSH, 0] }, // 90° E
  { anchor: 'top-left', offset: [DIAG, DIAG] }, // 135° SE
  { anchor: 'top', offset: [0, PUSH] }, // 180° S
  { anchor: 'top-right', offset: [-DIAG, DIAG] }, // 225° SW
  { anchor: 'right', offset: [-PUSH, 0] }, // 270° W
  { anchor: 'bottom-right', offset: [-DIAG, -DIAG] }, // 315° NW
];

/**
 * Pick a Marker anchor + offset that places a label OPPOSITE the direction a
 * compass cone points. Returns the default (label above the dot) when heading
 * is missing.
 *
 * `bearing` is the map's bearing (the compass direction that is screen-up). The
 * cones render with `icon-rotation-alignment: 'map'`, so a cone's on-screen
 * angle is `heading - bearing`. We subtract the bearing before quantizing so the
 * label sits opposite the cone in SCREEN space, not just in geographic space.
 */
export function labelPositionForHeading(heading: number | undefined | null, bearing = 0): LabelPosition {
  if (heading == null || !Number.isFinite(heading)) {
    return DEFAULT_POSITION;
  }
  const screenHeading = heading - (Number.isFinite(bearing) ? bearing : 0);
  const opposite = (((screenHeading + 180) % 360) + 360) % 360;
  const idx = Math.round(opposite / 45) % 8;
  return OCTANTS[idx];
}
